// When an admin alert subscriber (order_alert_subscriptions) is sent to: their ACTIVE
// window — the hours and the days of the week they want alerts, on the org's clock.
// The columns have always held an active window (`active_start_time`–`active_end_time`
// is when alerts ARE sent); the Settings dialog called it "quiet hours" until
// 2026-10-16, when Jon asked for days too and for the words to say what the data does.
// Pure functions only; shared by the alert service (server) and Settings (client).
import { getCustomDaysFromBitmask, getDayName, wallClockInTimezone } from "./periods";

export const EVERY_DAY = 0b1111111; // bits 0 (Sun) … 6 (Sat)

export type ActiveWindow = {
  /** "HH:MM" (Postgres `time` reads back "HH:MM:SS"); null = any hour. Both or neither. */
  activeStartTime?: string | null;
  activeEndTime?: string | null;
  /** Day bitmask, bit 0 = Sunday; null = every day. */
  activeDays?: number | null;
};

const minutesOf = (time: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return Number(match[1]) < 24 && Number(match[2]) < 60 ? minutes : null;
};

/**
 * Whether `instant` falls in the subscriber's active window, read on the org's clock
 * (`timezone`). No hours set = any hour; no days set = every day. An overnight window
 * (20:00–06:00) belongs to the day it starts on: a subscriber active Friday 20:00–06:00
 * is alerted at 02:00 Saturday, and not at 02:00 Friday.
 */
export function isWithinActiveWindow(window: ActiveWindow, instant: Date, timezone: string): boolean {
  const wall = wallClockInTimezone(instant, timezone);
  const now = wall.getUTCHours() * 60 + wall.getUTCMinutes();
  let weekday = wall.getUTCDay();

  const start = window.activeStartTime ? minutesOf(window.activeStartTime) : null;
  const end = window.activeEndTime ? minutesOf(window.activeEndTime) : null;
  if (start !== null && end !== null) {
    if (start <= end) {
      if (now < start || now >= end) return false;
    } else {
      // Overnight: before the end it is still the window that began yesterday.
      if (now >= end && now < start) return false;
      if (now < end) weekday = (weekday + 6) % 7;
    }
  }

  const days = window.activeDays ?? null;
  if (days === null) return true;
  return (days & (1 << weekday)) !== 0;
}

/** "HH:MM" from a stored time ("06:00:00") or a typed one ("6:00"). */
export function shortTime(time: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : time;
}

/**
 * The window in words, for the subscriber's card: "Active always", "Active 06:00 – 20:00",
 * "Active Mon – Fri", "Active Mon, Wed, Fri 06:00 – 20:00". A run of three or more
 * consecutive days reads as a range.
 */
export function describeActiveWindow(window: ActiveWindow): string {
  const parts: string[] = [];
  const days = window.activeDays ?? null;
  if (days !== null && days !== EVERY_DAY) {
    const chosen = getCustomDaysFromBitmask(days);
    if (chosen.length === 0) return "Never (no days chosen)";
    parts.push(dayRanges(chosen));
  }
  if (window.activeStartTime && window.activeEndTime) {
    parts.push(`${shortTime(window.activeStartTime)} – ${shortTime(window.activeEndTime)}`);
  }
  return parts.length === 0 ? "Active always" : `Active ${parts.join(" ")}`;
}

function dayRanges(days: number[]): string {
  const runs: number[][] = [];
  for (const day of days) {
    const run = runs[runs.length - 1];
    if (run && run[run.length - 1] === day - 1) run.push(day);
    else runs.push([day]);
  }
  return runs
    .map((run) => (run.length >= 3 ? `${getDayName(run[0])} – ${getDayName(run[run.length - 1])}` : run.map((d) => getDayName(d)).join(", ")))
    .join(", ");
}
